import React, { useState, useEffect } from "react";
import "./SupportTickets.css";
import SidebarTopbar from "../../components/layouts/layout";
import Alert from "@mui/material/Alert";
import Stack from "@mui/material/Stack";
import { FiArrowLeft } from "react-icons/fi";

const BASE_URL = process.env.REACT_APP_BASE_URL;

const SupportTickets = () => {
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    category: "Data Missing - Data is",
    urgency: "MEDIUM",
    due_date: "",
    file: null,
  });
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [alert, setAlert] = useState(null);
  
  // Ticket detail view state
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [ticketDetails, setTicketDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [newComment, setNewComment] = useState("");
  const [addingComment, setAddingComment] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);

  const urgencyOptions = [
    { value: "LOW", label: "Low" },
    { value: "MEDIUM", label: "Medium" },
    { value: "HIGH", label: "High" },
  ];

  const categoryOptions = [
    { value: "Data Missing - Data is", label: "Data Missing - Data is" },
    { value: "Bug - Something Broken", label: "Bug - Something Broken" },
    { value: "Feature Request", label: "Feature Request" },
    { value: "Performance Issue", label: "Performance Issue" },
    { value: "UI/UX Issue", label: "UI/UX Issue" },
    { value: "Other", label: "Other" },
  ];

  const token = localStorage.getItem("authToken");

  // Fetch tickets on mount
  useEffect(() => {
    fetchTickets();
  }, []);

  // Auto-dismiss alert after 5 seconds
  useEffect(() => {
    if (alert) {
      const timer = setTimeout(() => {
        setAlert(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [alert]);

  const fetchTickets = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${BASE_URL}/user_tickets`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error("Failed to fetch tickets");
      }

      const data = await response.json();
      setTickets(data.tickets || []);
    } catch (err) {
      console.error("Error fetching tickets:", err);
      setAlert({ severity: "error", message: "Failed to load tickets. Please try again." });
    } finally {
      setLoading(false);
    }
  };

  // Fetch ticket details
  const fetchTicketDetails = async (ticketId) => {
    // Extract numeric ID from ticket_id like "SR#4"
    const numericId = ticketId.replaceAll(/\D/g, "");
    setLoadingDetails(true);
    try {
      const response = await fetch(`${BASE_URL}/get_ticket_details?ticket_id=${numericId}`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error("Failed to fetch ticket details");
      }

      const data = await response.json();
      setTicketDetails(data.ticket);
    } catch (err) {
      console.error("Error fetching ticket details:", err);
      setAlert({ severity: "error", message: "Failed to load ticket details." });
    } finally {
      setLoadingDetails(false);
    }
  };

  // Update ticket status
  const updateTicketStatus = async (status) => {
    if (!ticketDetails) return;
    const numericId = ticketDetails.ticket_id.replaceAll(/\D/g, "");
    setUpdatingStatus(true);
    try {
      const formData = new FormData();
      formData.append("ticket_id", numericId);
      formData.append("status", status);

      const response = await fetch(`${BASE_URL}/update_ticket_status`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      });

      if (!response.ok) {
        throw new Error("Failed to update status");
      }

      setAlert({ severity: "success", message: "Status updated successfully!" });
      // Refresh ticket details and list
      fetchTicketDetails(ticketDetails.ticket_id);
      fetchTickets();
    } catch (err) {
      console.error("Error updating status:", err);
      setAlert({ severity: "error", message: "Failed to update status." });
    } finally {
      setUpdatingStatus(false);
    }
  };

  // Add comment to ticket
  const addComment = async () => {
    if (!newComment.trim() || !ticketDetails) return;
    const numericId = ticketDetails.ticket_id.replaceAll(/\D/g, "");
    setAddingComment(true);
    try {
      const formData = new FormData();
      formData.append("ticket_id", numericId);
      formData.append("content", newComment);

      const response = await fetch(`${BASE_URL}/comment_ticket`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      });

      if (!response.ok) {
        throw new Error("Failed to add comment");
      }

      setAlert({ severity: "success", message: "Comment added successfully!" });
      setNewComment("");
      // Refresh ticket details
      fetchTicketDetails(ticketDetails.ticket_id);
    } catch (err) {
      console.error("Error adding comment:", err);
      setAlert({ severity: "error", message: "Failed to add comment." });
    } finally {
      setAddingComment(false);
    }
  };

  // Handle ticket row click
  const handleTicketClick = (ticket) => {
    setSelectedTicket(ticket.ticket_id);
    fetchTicketDetails(ticket.ticket_id);
  };

  // Back to tickets list
  const handleBackToTickets = () => {
    setSelectedTicket(null);
    setTicketDetails(null);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    setFormData((prev) => ({
      ...prev,
      file: file || null,
    }));
  };

  const handlePaste = (e) => {
    const items = e.clipboardData?.items;
    if (items) {
      for (const item of items) {
        if (item.type.includes("image")) {
          const blob = item.getAsFile();
          setFormData((prev) => ({
            ...prev,
            file: blob,
          }));
          break;
        }
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setAlert(null);

    if (!formData.title.trim()) {
      setAlert({ severity: "error", message: "Quick Summary is required" });
      return;
    }
    if (!formData.description.trim()) {
      setAlert({ severity: "error", message: "The Full Story is required" });
      return;
    }

    setSubmitting(true);

    try {
      const submitData = new FormData();
      submitData.append("title", formData.title);
      submitData.append("description", formData.description);
      submitData.append("category", formData.category);
      submitData.append("urgency", formData.urgency);
      if (formData.due_date) {
        submitData.append("due_date", formData.due_date);
      }
      if (formData.file) {
        submitData.append("file", formData.file);
      }

      const response = await fetch(`${BASE_URL}/create_ticket`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: submitData,
      });

      if (!response.ok) {
        throw new Error("Failed to create ticket");
      }

      await response.json();
      setAlert({ severity: "success", message: "Ticket created successfully!" });
      
      // Reset form
      setFormData({
        title: "",
        description: "",
        category: "Data Missing - Data is",
        urgency: "MEDIUM",
        due_date: "",
        file: null,
      });

      // Refresh tickets list
      fetchTickets();
    } catch (err) {
      console.error("Error creating ticket:", err);
      setAlert({ severity: "error", message: "Failed to create ticket. Please try again." });
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const getStatusClass = (status) => {
    switch (status?.toUpperCase()) {
      case "OPEN":
        return "status-open";
      case "CLOSED":
        return "status-closed";
      case "IN_PROGRESS":
        return "status-in-progress";
      default:
        return "";
    }
  };

  const getUrgencyClass = (urgency) => {
    switch (urgency?.toUpperCase()) {
      case "HIGH":
        return "urgency-high";
      case "MEDIUM":
        return "urgency-medium";
      case "LOW":
        return "urgency-low";
      default:
        return "";
    }
  };

  const getStatusDisplayText = (status) => {
    switch (status?.toUpperCase()) {
      case "OPEN":
        return "OPEN";
      case "CLOSED":
        return "RESOLVED";
      case "IN_PROGRESS":
        return "IN PROGRESS";
      default:
        return status || "";
    }
  };

  // Render ticket detail view
  if (selectedTicket) {
    return (
      <SidebarTopbar>
        {alert && (
          <Stack sx={{ position: "fixed", top: 70, right: 20, zIndex: 9999 }} spacing={2}>
            <Alert
              severity={alert.severity}
              onClose={() => setAlert(null)}
              sx={{ width: "300px" }}
            >
              {alert.message}
            </Alert>
          </Stack>
        )}
        <div className="support-tickets-container">
          {loadingDetails && (
            <div className="loading-spinner">Loading ticket details...</div>
          )}
          {!loadingDetails && ticketDetails && (
            <div className="ticket-detail-view">
              {/* Back to Tickets */}
              <button className="back-to-tickets" onClick={handleBackToTickets}>
                <FiArrowLeft /> Back to Tickets
              </button>

              {/* Ticket ID Badge */}
              <div className="ticket-id-badge">{ticketDetails.ticket_id}</div>

              {/* Main Ticket Card */}
              <div className="ticket-detail-card">
                {/* Status and Urgency Badges */}
                <div className="ticket-badges">
                  <span className={`status-badge-detail ${getStatusClass(ticketDetails.status)}`}>
                    {getStatusDisplayText(ticketDetails.status)}
                  </span>
                  <span className={`urgency-badge-detail ${getUrgencyClass(ticketDetails.urgency)}`}>
                    {ticketDetails.urgency}
                  </span>
                </div>

                {/* Title */}
                <h2 className="ticket-detail-title">{ticketDetails.title}</h2>

                {/* Description */}
                <div className="ticket-detail-section">
                  <span className="detail-label">Description</span>
                  <p className="detail-value">{ticketDetails.description}</p>
                </div>

                {/* Reported On */}
                <div className="ticket-dates-row">
                  <div className="date-item">
                    <span className="detail-label">Reported On</span>
                    <p className="detail-value">{formatDate(ticketDetails.created_at)}</p>
                  </div>
                </div>

                {/* Issue Status Section */}
                <div className="issue-status-section">
                  <h3 className="issue-status-title">Issue Status</h3>
                  <p className="issue-status-description">
                    Update the status of this ticket based on the current resolution state.
                  </p>
                  <div className="status-buttons">
                    <button
                      className="status-btn resolved-btn"
                      onClick={() => updateTicketStatus("CLOSED")}
                      disabled={updatingStatus || ticketDetails.status?.toUpperCase() === "CLOSED"}
                    >
                      ✓ Mark as Resolved
                    </button>
                    <button
                      className="status-btn not-resolved-btn"
                      onClick={() => updateTicketStatus("OPEN")}
                      disabled={updatingStatus || ticketDetails.status?.toUpperCase() === "OPEN"}
                    >
                      ✕ Not yet Resolved
                    </button>
                  </div>
                </div>
              </div>

              {/* Comments Section */}
              <div className="comments-section">
                <div className="comments-header">
                  <h3 className="comments-title">Comments</h3>
                  <span className="comments-count">{ticketDetails.comments?.length || 0}</span>
                </div>

                {/* Comments List */}
                <div className="comments-list">
                  {ticketDetails.comments?.length === 0 ? (
                    <p className="no-comments">No comments yet.</p>
                  ) : (
                    ticketDetails.comments?.map((comment) => (
                      <div key={comment.comment_id} className="comment-card">
                        <div className="comment-header">
                          <span className="comment-author">{comment.user_name}</span>
                          <span className="comment-date">{formatDate(comment.created_at)}</span>
                        </div>
                        <p className="comment-content">{comment.content}</p>
                      </div>
                    ))
                  )}
                </div>

                {/* Add Comment */}
                <div className="add-comment-section">
                  <textarea
                    className="comment-input"
                    placeholder="Add a comment..."
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    rows={3}
                  />
                  <button
                    className="add-comment-btn"
                    onClick={addComment}
                    disabled={addingComment || !newComment.trim()}
                  >
                    {addingComment ? "Adding..." : "Add Comment"}
                  </button>
                </div>
              </div>
            </div>
          )}
          {!loadingDetails && !ticketDetails && (
            <div className="no-tickets">Ticket not found.</div>
          )}
        </div>
      </SidebarTopbar>
    );
  }

  return (
    <SidebarTopbar>
      {alert && (
        <Stack sx={{ position: "fixed", top: 70, right: 20, zIndex: 9999 }} spacing={2}>
          <Alert
            severity={alert.severity}
            onClose={() => setAlert(null)}
            sx={{ width: "300px" }}
          >
            {alert.message}
          </Alert>
        </Stack>
      )}
      <div className="support-tickets-container" onPaste={handlePaste}>
        {/* Report an Issue Form */}
        <div className="report-issue-section">
          <h2 className="section-title">
            <span className="title-icon">📋</span> Report an Issue
          </h2>

          <form onSubmit={handleSubmit} className="issue-form">
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="urgency">Urgency Level</label>
                <select
                  id="urgency"
                  name="urgency"
                  value={formData.urgency}
                  onChange={handleInputChange}
                  className="form-select"
                >
                  {urgencyOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="category">Nature of the Beast</label>
                <select
                  id="category"
                  name="category"
                  value={formData.category}
                  onChange={handleInputChange}
                  className="form-select"
                >
                  {categoryOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="title">
                Quick Summary (The Gist) <span className="required">*</span>
              </label>
              <input
                type="text"
                id="title"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                placeholder="e.g., User profile picture turns into a cat"
                className="form-input"
              />
            </div>

            <div className="form-group">
              <label htmlFor="description">
                The Full Story (Details, Details!) <span className="required">*</span>
              </label>
              <textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                placeholder="Please describe the issue with as much detail as possible. What were you doing? What did you expect? What happened instead?"
                className="form-textarea"
                rows={5}
              />
            </div>

            <div className="form-group">
              <label htmlFor="file-upload">
                Visual Evidence? (Optional Screenshot) 📷
              </label>
              <div className="file-upload-tip">
                <span className="tip-icon">💡</span>
                <em>Tip: Take a screenshot and press Ctrl+V anywhere to paste it!</em>
              </div>
              <div className="file-upload-wrapper">
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleFileChange}
                  className="file-input"
                  id="file-upload"
                />
                <label htmlFor="file-upload" className="file-upload-btn">
                  Choose File
                </label>
                <span className="file-name">
                  {formData.file ? formData.file.name : "No file chosen"}
                </span>
              </div>
            </div>

            <button
              type="submit"
              className="submit-btn"
              disabled={submitting}
            >
              {submitting ? "Submitting..." : "🚀 Launch Ticket!"}
            </button>
          </form>
        </div>

        {/* Tickets List Table */}
        <div className="tickets-list-section">
          {loading && (
            <div className="loading-spinner">Loading tickets...</div>
          )}
          {!loading && tickets.length === 0 && (
            <div className="no-tickets">No tickets found. Create your first ticket above!</div>
          )}
          {!loading && tickets.length > 0 && (
            <div className="tickets-table-wrapper">
              <table className="tickets-table">
                <thead>
                  <tr>
                    <th>TICKET ID</th>
                    <th>GIST</th>
                    <th>STATUS</th>
                    <th>URGENCY</th>
                    <th>REPORTED ON</th>
                  </tr>
                </thead>
                <tbody>
                  {tickets.map((ticket) => (
                    <tr 
                      key={ticket.ticket_id} 
                      onClick={() => handleTicketClick(ticket)}
                      className="ticket-row"
                    >
                      <td className="ticket-id">{ticket.ticket_id}</td>
                      <td className="ticket-title">{ticket.title}</td>
                      <td>
                        <span className={`status-badge ${getStatusClass(ticket.status)}`}>
                          {ticket.status}
                        </span>
                      </td>
                      <td>
                        <span className={`urgency-badge ${getUrgencyClass(ticket.urgency)}`}>
                          {ticket.urgency}
                        </span>
                      </td>
                      <td className="ticket-date">{formatDate(ticket.created_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </SidebarTopbar>
  );
};

export default SupportTickets;
